// AuditLogsFilters.tsx
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
} from '@mui/material';
import React, { useState } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { useIntl, defineMessages } from 'react-intl';

import CustomRangeDialog from '@components/DateRange';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';

import type { EventType } from '@constants/event_types';
import { EventFriendlyText } from '@constants/event_types';

import type { AuditLogFilterInput } from 'generated/types';

const messages = defineMessages({
  userFilter: {
    id: 'auditLogTable.userFilter',
    defaultMessage: 'User',
  },
  eventTypeFilter: {
    id: 'auditLogTable.eventTypeFilter',
    defaultMessage: 'Event Type',
  },
  dateRangeFilter: {
    id: 'auditLogTable.dateRangeFilter',
    defaultMessage: 'Date Range',
  },
  dateRangeApplyFilter: {
    id: 'auditLogTable.dateRangeApplyFilter',
    defaultMessage: 'Apply Filters',
  },
  resetFilter: {
    id: 'auditLogTable.resetFilter',
    defaultMessage: 'Reset Filters',
  },
  selectOption: {
    id: 'auditLogTable.select',
    defaultMessage: 'Select',
  },
  allTime: {
    id: 'auditLogTable.allTime',
    defaultMessage: 'All Time',
  },
  lastHour: {
    id: 'auditLogTable.lastHour',
    defaultMessage: 'Last Hour',
  },
  lastDay: {
    id: 'auditLogTable.lastDay',
    defaultMessage: 'Last Day',
  },
  lastWeek: {
    id: 'auditLogTable.lastWeek',
    defaultMessage: 'Last Week',
  },
  lastMonth: {
    id: 'auditLogTable.lastMonth',
    defaultMessage: 'Last Month',
  },
  lastYear: {
    id: 'auditLogTable.lastYear',
    defaultMessage: 'Last Year',
  },
  custom: {
    id: 'auditLogTable.custom',
    defaultMessage: 'Custom',
  },
});

interface AuditLogsFiltersProps {
  allUsers: { fullName?: string | null; userId: string }[];
  allEventTypes: string[];
  onApplyFilters: (filters: AuditLogFilterInput) => void;
  setPage: (page: number) => void;
}

const AuditLogsFilters: React.FC<AuditLogsFiltersProps> = ({
  allUsers,
  allEventTypes,
  onApplyFilters,
  setPage,
}) => {
  const { formatMessage } = useIntl();

  const DATE_OPTIONS = [
    { key: 'ALL_TIME', label: formatMessage(messages.allTime) },
    { key: 'LAST_HOUR', label: formatMessage(messages.lastHour) },
    { key: 'LAST_DAY', label: formatMessage(messages.lastDay) },
    { key: 'LAST_WEEK', label: formatMessage(messages.lastWeek) },
    { key: 'LAST_MONTH', label: formatMessage(messages.lastMonth) },
    { key: 'LAST_YEAR', label: formatMessage(messages.lastYear) },
    { key: 'CUSTOM', label: formatMessage(messages.custom) },
  ];
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([]);
  const [selectedDateOption, setSelectedDateOption] = useState<string>('');
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);
  const [customOpen, setCustomOpen] = useState(false);

  const handleApplyCustomRange = (start: Date, end: Date) => {
    setCustomStartDate(start);
    setCustomEndDate(end);
    setCustomOpen(false);
  };

  const getDateRange = (option: string) => {
    const now = new Date();
    switch (option) {
      case 'LAST_HOUR':
        return {
          startDate: new Date(now.getTime() - 1 * 60 * 60 * 1000),
          endDate: now,
        };
      case 'LAST_DAY':
        return {
          startDate: new Date(now.getTime() - 24 * 60 * 60 * 1000),
          endDate: now,
        };
      case 'LAST_WEEK':
        return {
          startDate: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
          endDate: now,
        };
      case 'LAST_MONTH':
        return {
          startDate: new Date(
            now.getFullYear(),
            now.getMonth() - 1,
            now.getDate()
          ),
          endDate: now,
        };
      case 'LAST_YEAR':
        return {
          startDate: new Date(
            now.getFullYear() - 1,
            now.getMonth(),
            now.getDate()
          ),
          endDate: now,
        };
      case 'CUSTOM':
        return { startDate: customStartDate, endDate: customEndDate };
      case 'ALL_TIME':
      default:
        return { startDate: null, endDate: null };
    }
  };

  const handleResetFilters = () => {
    setSelectedUserIds([]);
    setSelectedEventTypes([]);
    setSelectedDateOption('');
    setCustomStartDate(null);
    setCustomEndDate(null);
    setPage(1);
  };

  return (
    <Box
      display="flex"
      gap={2}
      flexWrap="wrap"
      marginBottom={2}
      alignItems="center"
    >
      <FormControl style={{ minWidth: 200, maxWidth: 200 }}>
        <InputLabel>{formatMessage(messages.eventTypeFilter)}</InputLabel>
        <Select
          multiple
          value={selectedEventTypes}
          onChange={(e) =>
            setSelectedEventTypes(
              typeof e.target.value === 'string'
                ? e.target.value.split(',')
                : e.target.value
            )
          }
          renderValue={(selected) =>
            selected.length
              ? (selected as string[])
                  .map((s) => EventFriendlyText[s as EventType])
                  .join(', ')
              : ''
          }
        >
          {allEventTypes.map((type) => (
            <MenuItem key={type} value={type}>
              <Checkbox checked={selectedEventTypes.includes(type)} />
              <ListItemText primary={EventFriendlyText[type as EventType]} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl style={{ minWidth: 150, maxWidth: 150 }}>
        <InputLabel>{formatMessage(messages.dateRangeFilter)}</InputLabel>
        <Select
          value={selectedDateOption}
          onChange={(e) => setSelectedDateOption(e.target.value)}
        >
          {DATE_OPTIONS.map((opt) => (
            <MenuItem
              key={opt.key}
              value={opt.key}
              onClick={() => opt.key === 'CUSTOM' && setCustomOpen(true)}
            >
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <CustomRangeDialog
        open={customOpen}
        onCancel={() => setCustomOpen(false)}
        onApply={handleApplyCustomRange}
      />
      <FormControl style={{ minWidth: 200, maxWidth: 200 }}>
        <InputLabel>{formatMessage(messages.userFilter)}</InputLabel>
        <Select
          multiple
          value={selectedUserIds}
          onChange={(e) => {
            const value = e.target.value;
            setSelectedUserIds(
              typeof value === 'string' ? value.split(',') : value
            );
          }}
          renderValue={(selected) =>
            (selected as string[])
              .map(
                (id) => allUsers.find((u) => u.userId === id)?.fullName || id
              )
              .join(', ')
          }
        >
          {allUsers.map((u) => (
            <MenuItem key={u.userId} value={u.userId}>
              <Checkbox checked={selectedUserIds.includes(u.userId)} />
              <ListItemText primary={u.fullName} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        variant="contained"
        color="primary"
        onClick={() => {
          setPage(1);
          const { startDate, endDate } = getDateRange(selectedDateOption);
          onApplyFilters({
            eventTypes: selectedEventTypes,
            userIds: selectedUserIds,
            startDate,
            endDate,
          });
        }}
      >
        {formatMessage(messages.dateRangeApplyFilter)}
      </Button>

      <Button variant="outlined" color="primary" onClick={handleResetFilters}>
        {formatMessage(messages.resetFilter)}
      </Button>
    </Box>
  );
};

export default withErrorBoundary(AuditLogsFilters, {
  FallbackComponent: ErrorBoundaryFallback,
});
